import type { Link } from '@wix/public-schemas'
import type {
	DocumentLinkData,
	DynamicPageLinkData,
	EmailLinkData,
	ExternalLinkData,
	PageLinkData,
	PhoneLinkData,
	TpaPageLinkData,
	WhatsAppLinkData,
	AddressLinkData,
	LoginToWixLinkData,
	AnchorLinkData,
} from './linksDataTypes'
import type { PageLinkExtraData } from './pageLink'
import { handlePageLink } from './pageLink'
import type { DocumentLinkExtraData } from './documentLink'
import { handleDocumentLink } from './documentLink'
import { handleExternalLink } from './externalLink'
import { handleEmailLink } from './emailLink'
import type { DynamicPageLinkExtraData } from './dynamicPageLink'
import { handleDynamicPageLink } from './dynamicPageLink'
import type { TpaPageLinkExtraData } from './tpaPageLink'
import { handleTpaPageLink } from './tpaPageLink'
import { handlePhoneLink } from './phoneLink'
import { handleWhatsAppLink } from './whatsAppLink'
import { handleAddressLink } from './addressLink'
import { handleLoginToWixLink } from './loginToWixLink'
import type { AnchorLinkExtraData } from './anchorLink'
import { handleAnchorLink } from './anchorLink'

// Define a type that maps link types to their required extra data
type LinkExtraDataMap = {
	DocumentLink: DocumentLinkExtraData
	PageLink: PageLinkExtraData
	EmailLink: never
	ExternalLink: never
	DynamicPageLink: DynamicPageLinkExtraData
	TpaPageLink: TpaPageLinkExtraData
	PhoneLink: never
	WhatsAppLink: never
	AddressLink: never
	LoginToWixLink: never
	AnchorLink: AnchorLinkExtraData
}

type ExtraDataForLink<T extends { type: keyof LinkExtraDataMap }> = LinkExtraDataMap[T['type']]

export type UnResolvedLink =
	| DocumentLinkData
	| EmailLinkData
	| ExternalLinkData
	| PageLinkData
	| DynamicPageLinkData
	| TpaPageLinkData
	| PhoneLinkData
	| WhatsAppLinkData
	| AddressLinkData
	| LoginToWixLinkData
	| AnchorLinkData

export type LinkResolver = (unResolvedLink: UnResolvedLink, extraData?: ExtraDataForLink<typeof unResolvedLink>) => Link

export const resolveLink: LinkResolver = (unResolvedLink, extraData) => {
	const type = unResolvedLink?.type
	if (!type) {
		return {} as Link
	}

	switch (type) {
		case 'DocumentLink':
			return handleDocumentLink(unResolvedLink, extraData as DocumentLinkExtraData)
		case 'EmailLink':
			return handleEmailLink(unResolvedLink)
		case 'ExternalLink':
			return handleExternalLink(unResolvedLink)
		case 'PageLink':
			return handlePageLink(unResolvedLink, extraData as PageLinkExtraData)
		case 'DynamicPageLink':
			return handleDynamicPageLink(unResolvedLink, extraData as DynamicPageLinkExtraData)
		case 'TpaPageLink':
			return handleTpaPageLink(unResolvedLink, extraData as TpaPageLinkExtraData)
		case 'PhoneLink':
			return handlePhoneLink(unResolvedLink)
		case 'WhatsAppLink':
			return handleWhatsAppLink(unResolvedLink)
		case 'AddressLink':
			return handleAddressLink(unResolvedLink)
		case 'LoginToWixLink':
			return handleLoginToWixLink(unResolvedLink)
		case 'AnchorLink':
			return handleAnchorLink(unResolvedLink, extraData as AnchorLinkExtraData)
		default:
			return {} as Link
	}
}
